import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import {
	AccountStage,
	IAdminAccount,
	IAdminOrganization,
	IAdminTeam,
	IAdminUser,
	createAccount,
	invalidateAccountQueries,
	updateAccount,
	useAdminOrganizationsQuery,
	useAdminTeamsQuery,
	useAdminUsersQuery,
} from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';

interface Props {
	open: boolean;
	onClose: () => void;
	/** When set, the dialog edits this account (organization is fixed); otherwise it creates one. */
	account?: IAdminAccount | null;
}

interface FieldErrors {
	organization?: string;
	name?: string;
	teams?: string;
	end_date?: string;
	seats?: string;
	value?: string;
}

const STAGES: { value: AccountStage; label: string; hint: string }[] = [
	{ value: 'prospect', label: 'Prospect', hint: 'in conversation, nothing signed' },
	{ value: 'trial', label: 'Trial', hint: 'evaluating; set the end date' },
	{ value: 'live', label: 'Live', hint: 'paying; set the end date' },
	{ value: 'churned', label: 'Churned', hint: 'was a customer, left' },
	{ value: 'lost', label: 'Lost', hint: 'never signed' },
];

/** Create or edit an account: organization, stage, covered teams, dates, seats, value, owner, notes. */
export default function AccountDialog({ open, onClose, account }: Props) {
	const queryClient = useQueryClient();
	const { data: organizations, isLoading: organizationsLoading } = useAdminOrganizationsQuery();
	const { data: allTeams, isLoading: teamsLoading } = useAdminTeamsQuery();
	// owner candidates: your own team, i.e. arbinsights logins
	const { data: allUsers } = useAdminUsersQuery({ enabled: open });
	const isEdit = !!account;

	const [organization, setOrganization] = useState<IAdminOrganization | null>(null);
	const [name, setName] = useState('');
	const [stage, setStage] = useState<AccountStage>('prospect');
	const [teams, setTeams] = useState<IAdminTeam[]>([]);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [seats, setSeats] = useState('');
	const [value, setValue] = useState('');
	const [owner, setOwner] = useState<IAdminUser | null>(null);
	const [notes, setNotes] = useState('');
	const [errors, setErrors] = useState<FieldErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const owners = (allUsers ?? []).filter((u) => u.is_active && u.email.toLowerCase().endsWith('@arbinsights.com'));

	// (Re)fill the form whenever the dialog opens or the reference data arrives.
	useEffect(() => {
		if (!open || !account) return;
		setName(account.name);
		setStage(account.stage);
		setStartDate(account.start_date ?? '');
		setEndDate(account.end_date ?? '');
		setSeats(account.seats?.toString() ?? '');
		setValue(account.value?.toString() ?? '');
		setNotes(account.notes ?? '');
		setOrganization(organizations?.find((o) => o.id === account.organization_id) ?? null);
		const ids = new Set(account.teams.map((t) => t.id));
		setTeams((allTeams ?? []).filter((t) => ids.has(t.id)));
		setOwner(owners.find((u) => u.id === account.owner_user_id) ?? null);
	}, [open, account, allTeams, organizations, allUsers]);

	function resetAndClose() {
		setOrganization(null);
		setName('');
		setStage('prospect');
		setTeams([]);
		setStartDate('');
		setEndDate('');
		setSeats('');
		setValue('');
		setOwner(null);
		setNotes('');
		setErrors({});
		setError(null);
		onClose();
	}

	const organizationId = account?.organization_id ?? organization?.id;
	const teamOptions = (allTeams ?? []).filter((t) => t.organization_id === organizationId);
	const needsTeams = stage === 'trial' || stage === 'live';

	function validate(): boolean {
		const next: FieldErrors = {};
		if (!organizationId) next.organization = 'Required';
		if (!name.trim()) next.name = 'Required';
		if (needsTeams && teams.length === 0) next.teams = 'A trial or live account must cover at least one team';
		if (endDate && startDate && endDate < startDate) next.end_date = 'Must be on or after the start date';
		if (seats && (!/^\d+$/.test(seats) || Number(seats) < 0)) next.seats = 'Whole number';
		if (value && (isNaN(Number(value)) || Number(value) < 0)) next.value = 'Number';
		setErrors(next);
		return Object.keys(next).length === 0;
	}

	async function handleSubmit() {
		if (!validate() || !organizationId) return;
		setSubmitting(true);
		setError(null);
		const fields = {
			name: name.trim(),
			stage,
			start_date: startDate || null,
			end_date: endDate || null,
			seats: seats ? Number(seats) : null,
			value: value ? Number(value) : null,
			owner_user_id: owner?.id ?? null,
			notes: notes.trim() || null,
			team_ids: teams.map((t) => t.id),
		};
		try {
			if (account) {
				await updateAccount(account.id, fields);
			} else {
				await createAccount({ ...fields, organization_id: organizationId });
			}
			await invalidateAccountQueries(queryClient);
			resetAndClose();
		} catch (e: any) {
			setError(apiErrorMessage(e, 'An unexpected error occurred. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundImage: 'none' } }}>
			<DialogTitle>{isEdit ? 'Edit Account' : 'New Account'}</DialogTitle>
			<DialogContent sx={{ pb: 1 }}>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				<Stack spacing={2} sx={{ mt: 0.5 }}>
					{isEdit && account ? (
						<Typography variant="body2" color="text.secondary">
							Organization: <strong>{account.organization_name}</strong>
						</Typography>
					) : (
						<Autocomplete
							options={organizations ?? []}
							loading={organizationsLoading}
							value={organization}
							onChange={(_, v) => {
								setOrganization(v);
								setTeams([]); // teams belong to one organization
								setErrors((e) => ({ ...e, organization: undefined }));
							}}
							getOptionLabel={(o) => o.name}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Organization"
									size="small"
									required
									error={!!errors.organization}
									helperText={errors.organization}
								/>
							)}
						/>
					)}
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							label="Account name"
							size="small"
							required
							placeholder="e.g. the pod or desk"
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								setErrors((er) => ({ ...er, name: undefined }));
							}}
							error={!!errors.name}
							helperText={errors.name}
							sx={{ flex: 1.4 }}
						/>
						<TextField
							select
							label="Stage"
							size="small"
							required
							value={stage}
							onChange={(e) => {
								setStage(e.target.value as AccountStage);
								setErrors((er) => ({ ...er, teams: undefined }));
							}}
							helperText={STAGES.find((s) => s.value === stage)?.hint}
							sx={{ flex: 1 }}
						>
							{STAGES.map((s) => (
								<MenuItem key={s.value} value={s.value}>
									{s.label}
								</MenuItem>
							))}
						</TextField>
					</Box>
					<Autocomplete
						multiple
						options={teamOptions}
						loading={teamsLoading}
						disableCloseOnSelect
						filterSelectedOptions
						disabled={!organizationId}
						value={teams}
						onChange={(_, v) => {
							setTeams(v);
							setErrors((e) => ({ ...e, teams: undefined }));
						}}
						getOptionLabel={(t) => t.name}
						isOptionEqualToValue={(a, b) => a.id === b.id}
						renderTags={(selected, getTagProps) =>
							selected.map((t, index) => <Chip {...getTagProps({ index })} label={t.name} size="small" />)
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Teams covered"
								size="small"
								required={needsTeams}
								placeholder={organizationId ? 'Pick one or more teams…' : 'Pick an organization first'}
								error={!!errors.teams}
								helperText={errors.teams ?? 'A team can be on only one active account at a time'}
							/>
						)}
					/>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							label="Start date"
							type="date"
							size="small"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
							sx={{ flex: 1 }}
						/>
						<TextField
							label={stage === 'trial' ? 'Trial ends' : 'End date'}
							type="date"
							size="small"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							error={!!errors.end_date}
							helperText={errors.end_date ?? 'blank = open-ended'}
							InputLabelProps={{ shrink: true }}
							sx={{ flex: 1 }}
						/>
					</Box>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							label="Seats"
							size="small"
							value={seats}
							onChange={(e) => setSeats(e.target.value)}
							error={!!errors.seats}
							helperText={errors.seats}
							inputProps={{ inputMode: 'numeric' }}
							sx={{ flex: 1 }}
						/>
						<TextField
							label="Value (total for the term)"
							size="small"
							value={value}
							onChange={(e) => setValue(e.target.value)}
							error={!!errors.value}
							helperText={errors.value}
							inputProps={{ inputMode: 'decimal' }}
							sx={{ flex: 1 }}
						/>
					</Box>
					<Autocomplete
						options={owners}
						value={owner}
						onChange={(_, v) => setOwner(v)}
						getOptionLabel={(u) => u.full_name ?? u.email}
						isOptionEqualToValue={(a, b) => a.id === b.id}
						renderInput={(params) => <TextField {...params} label="Owner (optional)" size="small" />}
					/>
					<TextField
						label="Notes"
						size="small"
						multiline
						minRows={3}
						placeholder="e.g. Products: feed · contact · next step"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={resetAndClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={submitting}
					startIcon={submitting ? <CircularProgress size={16} /> : undefined}
				>
					{submitting ? 'Saving…' : isEdit ? 'Save' : 'Create Account'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
